import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LogIn, LogOut, Grid, Home, MessageSquarePlus, Menu, X, User, Shield } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { tid } from "@/lib/testids";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function TopNav() {
  const { user, status, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const onLogin = () => {
    // Store current path to return to after login
    sessionStorage.setItem('loginReturnUrl', window.location.pathname);
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const onLogout = async () => {
    await logout();
    if (location.pathname.startsWith("/q/")) return;
    navigate("/");
  };

  const isActive = (path) => location.pathname === path;

  const NavLink = ({ to, children, icon: Icon, testId }) => (
    <Link
      to={to}
      data-testid={testId}
      className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200 ${
        isActive(to)
          ? "bg-indigo-500/20 text-indigo-300"
          : "text-slate-400 hover:bg-slate-800 hover:text-slate-200"
      }`}
      onClick={() => setMobileMenuOpen(false)}
    >
      {Icon && <Icon className="h-4 w-4" />}
      {children}
    </Link>
  );

  return (
    <>
      {/* Desktop Header */}
      <header
        data-testid={tid.topnav}
        className="sticky top-0 z-20 border-b border-slate-800 bg-slate-900/95 backdrop-blur-sm"
      >
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          {/* Logo */}
          <Link
            to="/"
            data-testid={tid.brandLink}
            className="flex items-center gap-2 group"
          >
            <span className="text-lg font-bold text-slate-200 group-hover:text-white transition-colors">
              askincog<span className="text-indigo-400">.com</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden items-center gap-1 md:flex">
            <NavLink to="/" icon={Home} testId={tid.askLink}>
              Home
            </NavLink>
            <NavLink to="/browse" icon={Grid} testId={tid.browseLink}>
              Browse
            </NavLink>
            {status === "authed" && (
              <NavLink to="/profile" icon={User} testId="profile-link">
                Profile
              </NavLink>
            )}
          </nav>

          {/* Right Side */}
          <div className="flex items-center gap-3">
            <div className="hidden md:block">
              <LanguageSwitcher />
            </div>

            {status === "authed" && user ? (
              <div className="hidden items-center gap-2 md:flex">
                <div className="flex items-center gap-2 rounded-full bg-slate-800 px-3 py-1.5 border border-slate-700">
                  <div className="w-6 h-6 rounded-full bg-indigo-500 flex items-center justify-center text-white text-xs font-bold">
                    {user.name ? user.name.charAt(0).toUpperCase() : "U"}
                  </div>
                  <span className="text-sm text-slate-300">
                    {user.pseudonym || user.name || "User"}
                  </span>
                </div>
                <Button
                  data-testid={tid.logoutButton}
                  variant="ghost"
                  size="sm"
                  className="h-8 text-slate-400 hover:text-slate-200 hover:bg-slate-800"
                  onClick={onLogout}
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <Button
                data-testid={tid.loginButton}
                size="sm"
                className="hidden h-9 rounded-lg bg-indigo-600 px-4 text-sm font-medium text-white hover:bg-indigo-500 md:flex btn-press"
                onClick={onLogin}
              >
                <LogIn className="mr-1.5 h-4 w-4" />
                {t("login")}
              </Button>
            )}

            {/* Mobile Menu Button */}
            <button
              className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-800 hover:text-slate-200 md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="border-t border-slate-800 bg-slate-900 px-4 pb-4 pt-2 md:hidden">
            <nav className="flex flex-col gap-1">
              <NavLink to="/" icon={Home} testId={tid.askLink}>
                Home
              </NavLink>
              <NavLink to="/browse" icon={Grid} testId={tid.browseLink}>
                Browse
              </NavLink>
              {status === "authed" && (
                <NavLink to="/profile" icon={User} testId="profile-link-mobile">
                  Profile
                </NavLink>
              )}
            </nav>
            
            <div className="mt-3 flex items-center justify-between border-t border-slate-800 pt-3">
              <LanguageSwitcher />
              
              {status === "authed" && user ? (
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2 text-sm text-slate-400">
                    <div className="w-6 h-6 rounded-full bg-indigo-500 flex items-center justify-center text-white text-xs font-bold">
                      {user.name ? user.name.charAt(0).toUpperCase() : "U"}
                    </div>
                    <span>{user.pseudonym || user.name}</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 text-slate-500 hover:text-slate-300"
                    onClick={() => { onLogout(); setMobileMenuOpen(false); }}
                  >
                    <LogOut className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <Button
                  size="sm"
                  className="h-8 rounded-lg bg-indigo-600 text-sm text-white hover:bg-indigo-500"
                  onClick={() => { onLogin(); setMobileMenuOpen(false); }}
                >
                  <LogIn className="mr-1.5 h-4 w-4" />
                  Login
                </Button>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Mobile Bottom Navigation - Fixed */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 border-t border-slate-800 bg-slate-900/95 backdrop-blur-sm md:hidden safe-bottom">
        <div className="flex items-center justify-around py-2">
          <Link
            to="/"
            className={`flex flex-col items-center gap-1 px-4 py-2 ${
              isActive("/") ? "text-indigo-400" : "text-slate-500"
            }`}
          >
            <Home className="h-5 w-5" />
            <span className="text-[10px] font-medium">Home</span>
          </Link>
          <Link
            to="/browse"
            className={`flex flex-col items-center gap-1 px-4 py-2 ${
              isActive("/browse") ? "text-indigo-400" : "text-slate-500"
            }`}
          >
            <Grid className="h-5 w-5" />
            <span className="text-[10px] font-medium">Browse</span>
          </Link>
          <button
            onClick={() => navigate("/")}
            className="flex flex-col items-center gap-1 px-4 py-2 text-indigo-400"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-600 -mt-4 shadow-lg shadow-indigo-500/30">
              <MessageSquarePlus className="h-5 w-5 text-white" />
            </div>
            <span className="text-[10px] font-medium">Ask</span>
          </button>
          <Link
            to="/profile"
            className={`flex flex-col items-center gap-1 px-4 py-2 ${
              isActive("/profile") ? "text-indigo-400" : "text-slate-500"
            }`}
          >
            <User className="h-5 w-5" />
            <span className="text-[10px] font-medium">Profile</span>
          </Link>
        </div>
      </nav>
    </>
  );
}
